import type {
  PaymentMethod,
  TreasuryAccount,
  TreasuryAccountType,
  TreasuryMovement,
} from "./types";
import { PAYMENT_METHOD_ACCOUNT_TYPES } from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";

const ACCOUNTS_KEY = "hospitol.treasuryAccounts";
const MOVEMENTS_KEY = "hospitol.treasuryMovements";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown, notify = true): void {
  setStorageItem(key, JSON.stringify(value));
  if (notify) {
    window.dispatchEvent(new CustomEvent("hospitol:treasury"));
  }
}

function seedAccounts(): TreasuryAccount[] {
  const seeded = defaultAccounts();
  writeJson(ACCOUNTS_KEY, seeded, false);
  return seeded;
}

function defaultAccounts(): TreasuryAccount[] {
  const now = new Date().toISOString();
  return [
    {
      id: "tr-ca-main",
      type: "cash",
      name: "Caisse principale",
      balance: 0,
      institution: "Établissement",
      accountNumber: "CAISSE-01",
      active: true,
      createdAt: now,
    },
    {
      id: "tr-mm-mpesa",
      type: "mobile_money",
      name: "Mobile Money M-Pesa",
      balance: 0,
      institution: "M-Pesa",
      accountNumber: "+243 900 000 001",
      active: true,
      createdAt: now,
    },
    {
      id: "tr-mm-orange",
      type: "mobile_money",
      name: "Mobile Money Orange",
      balance: 0,
      institution: "Orange Money",
      accountNumber: "+243 800 000 001",
      active: true,
      createdAt: now,
    },
    {
      id: "tr-bk-rawbank",
      type: "bank",
      name: "Compte RAWBANK",
      balance: 0,
      institution: "RAWBANK",
      accountNumber: "001-4521-00",
      active: true,
      createdAt: now,
    },
  ];
}

function normalizeAccount(raw: Record<string, unknown>): TreasuryAccount {
  return {
    id: String(raw.id ?? ""),
    type: (raw.type as TreasuryAccountType) ?? "cash",
    name: String(raw.name ?? ""),
    balance: Number(raw.balance ?? 0),
    institution: String(raw.institution ?? ""),
    accountNumber: String(raw.accountNumber ?? ""),
    active: raw.active !== false,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
  };
}

function readAccounts(): TreasuryAccount[] {
  const list = readJson<unknown>(ACCOUNTS_KEY, []);
  if (!Array.isArray(list) || list.length === 0) {
    return seedAccounts();
  }
  try {
    return list.map((item) => normalizeAccount(item as Record<string, unknown>));
  } catch {
    return seedAccounts();
  }
}

function readMovements(): TreasuryMovement[] {
  const list = readJson<unknown>(MOVEMENTS_KEY, []);
  return Array.isArray(list) ? (list as TreasuryMovement[]) : [];
}

export function listTreasuryAccounts(filter?: {
  type?: TreasuryAccountType;
  activeOnly?: boolean;
}): TreasuryAccount[] {
  let list = readAccounts();
  if (filter?.type) list = list.filter((a) => a.type === filter.type);
  if (filter?.activeOnly !== false) list = list.filter((a) => a.active);
  return list.sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

export function getTreasuryAccount(id: string): TreasuryAccount | null {
  return readAccounts().find((a) => a.id === id) ?? null;
}

export function listAccountsForPaymentMethod(method: PaymentMethod): TreasuryAccount[] {
  const types = PAYMENT_METHOD_ACCOUNT_TYPES[method];
  return listTreasuryAccounts().filter((a) => types.includes(a.type));
}

export function listTreasuryMovements(filter?: {
  accountId?: string;
  limit?: number;
}): TreasuryMovement[] {
  let list = readMovements();
  if (filter?.accountId) list = list.filter((m) => m.accountId === filter.accountId);
  list = list.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  if (filter?.limit) list = list.slice(0, filter.limit);
  return list;
}

export function creditTreasuryAccount(input: {
  accountId: string;
  amount: number;
  label: string;
  billingDocumentId?: string | null;
  paymentMethod: PaymentMethod;
  reference?: string;
}): { account: TreasuryAccount; movement: TreasuryMovement } | null {
  if (input.amount <= 0) return null;

  const accounts = readAccounts();
  const index = accounts.findIndex((a) => a.id === input.accountId);
  if (index < 0) return null;

  const now = new Date().toISOString();
  const movement: TreasuryMovement = {
    id: `tm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    accountId: input.accountId,
    type: "credit",
    amount: input.amount,
    label: input.label,
    billingDocumentId: input.billingDocumentId ?? null,
    paymentMethod: input.paymentMethod,
    reference: input.reference?.trim() ?? "",
    createdAt: now,
  };

  accounts[index] = {
    ...accounts[index]!,
    balance: accounts[index]!.balance + input.amount,
  };

  const movements = readMovements();
  movements.unshift(movement);
  writeJson(ACCOUNTS_KEY, accounts);
  writeJson(MOVEMENTS_KEY, movements);
  return { account: accounts[index]!, movement };
}

export function totalTreasuryBalance(): number {
  return listTreasuryAccounts().reduce((sum, a) => sum + a.balance, 0);
}
